import { Controller } from "@hotwired/stimulus"

// Animated login background: mesh gradient + bezier paths with travelling dots.
// Attach to a <canvas> element with data-controller="login-background".
export default class extends Controller {
  connect() {
    this.ctx           = this.element.getContext("2d")
    this.animFrameId   = null
    this.lastTime      = 0
    this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    this.boundFrame    = this.#frame.bind(this)
    this.boundResize   = this.#resize.bind(this)

    window.addEventListener("resize", this.boundResize)
    this.#resize()
    if (!this.reducedMotion) {
      this.animFrameId = requestAnimationFrame(this.boundFrame)
    }
  }

  disconnect() {
    cancelAnimationFrame(this.animFrameId)
    window.removeEventListener("resize", this.boundResize)
  }

  // ── Private ────────────────────────────────────────────────────────────

  #resize() {
    // backing store in device pixels so lines stay crisp on retina;
    // all drawing maths stays in CSS pixels via the transform
    this.dpr = Math.min(window.devicePixelRatio || 1, 2)
    this.W   = window.innerWidth
    this.H   = window.innerHeight
    this.element.width  = this.W * this.dpr
    this.element.height = this.H * this.dpr
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0)

    this.#generateScene()
    this.#buildNoiseTexture()
    if (this.reducedMotion) this.#renderFrame()
  }

  // Pre-bakes N noise textures at resize time and cycles through them
  // every frame (temporal dithering). Cycling prevents the eye from
  // locking onto a fixed grain pattern, making banding nearly invisible.
  // Kept at CSS-pixel resolution deliberately: full-retina noise frames
  // cost ~4x the memory for no visible gain at 8% opacity.
  #buildNoiseTexture() {
    const count    = 4
    this.noiseFrames = Array.from({ length: count }, () => {
      const off    = document.createElement("canvas")
      off.width    = this.W
      off.height   = this.H
      const offCtx = off.getContext("2d")
      const img    = offCtx.createImageData(this.W, this.H)
      const data   = img.data
      for (let i = 0; i < data.length; i += 4) {
        const v = Math.floor(Math.random() * 255)
        data[i] = data[i + 1] = data[i + 2] = v
        data[i + 3] = 255
      }
      offCtx.putImageData(img, 0, 0)
      return off
    })
    this.noiseIndex = 0
  }

  #generateScene() {
    const W = this.W, H = this.H

    // Two irregular elliptical blobs — rendered via scale+rotate transforms
    // so each has an organic, asymmetric shape rather than a perfect circle.
    this.meshBlobs = [
      { x: W * 0.18, y: H * 0.55, r: W * 0.32, scaleY: 0.60, rotation: -0.35 },
      { x: W * 0.80, y: H * 0.30, r: W * 0.30, scaleY: 0.55, rotation:  0.50 },
    ]

    // Six bezier paths, each with 1–2 dots travelling along them
    this.paths = Array.from({ length: 6 }, () => {
      const p0 = this.#edgePoint()
      const p3 = this.#edgePoint()
      const { p1, p2 } = this.#smoothControlPoints(p0, p3)

      // Each dot takes ~12–20 s to traverse the full path
      const speed = 0.00005 + Math.random() * 0.000033

      const numDots = Math.random() < 0.35 ? 2 : 1
      return {
        p0, p1, p2, p3, speed,
        dots: Array.from({ length: numDots }, () => ({
          t:    Math.random(),                      // random starting position
          size: 1.5 + Math.random() * 1.0,
        })),
      }
    })
  }

  // Returns a random point just outside one of the four viewport edges
  #edgePoint() {
    const { W, H } = this
    const edge = Math.floor(Math.random() * 4)
    const along = () => 0.1 + Math.random() * 0.8
    if (edge === 0) return { x: along() * W, y: -10 }
    if (edge === 1) return { x: along() * W, y: H + 10 }
    if (edge === 2) return { x: -10,         y: along() * H }
    return               { x: W + 10,        y: along() * H }
  }

  // Generates cubic bezier control points constrained to a gentle arc —
  // control points are placed along the p0→p3 axis with a limited lateral
  // swing (max 35% of chord length), preventing sharp hairpin bends.
  #smoothControlPoints(p0, p3) {
    const dx = p3.x - p0.x
    const dy = p3.y - p0.y
    const dist = Math.sqrt(dx * dx + dy * dy)
    const nx = dx / dist, ny = dy / dist   // forward unit vector
    const px = -ny,       py = nx          // perpendicular unit vector
    const swing = () => (Math.random() * 2 - 1) * 0.35

    return {
      p1: {
        x: p0.x + nx * dist * (0.25 + Math.random() * 0.2) + px * dist * swing(),
        y: p0.y + ny * dist * (0.25 + Math.random() * 0.2) + py * dist * swing(),
      },
      p2: {
        x: p3.x - nx * dist * (0.25 + Math.random() * 0.2) + px * dist * swing(),
        y: p3.y - ny * dist * (0.25 + Math.random() * 0.2) + py * dist * swing(),
      },
    }
  }

  #bezierPoint(p0, p1, p2, p3, t) {
    const u = 1 - t
    return {
      x: u*u*u*p0.x + 3*u*u*t*p1.x + 3*u*t*t*p2.x + t*t*t*p3.x,
      y: u*u*u*p0.y + 3*u*u*t*p1.y + 3*u*t*t*p2.y + t*t*t*p3.y,
    }
  }

  #drawMesh() {
    const { ctx, W, H } = this
    ctx.fillStyle = "#162538"
    ctx.fillRect(0, 0, W, H)

    for (const blob of this.meshBlobs) {
      ctx.save()
      ctx.translate(blob.x, blob.y)
      ctx.rotate(blob.rotation)
      ctx.scale(1, blob.scaleY)
      const grad = ctx.createRadialGradient(0, 0, 0, 0, 0, blob.r)
      grad.addColorStop(0,   "rgba(28, 56, 91, 0.60)")
      grad.addColorStop(0.5, "rgba(28, 56, 91, 0.20)")
      grad.addColorStop(1,   "rgba(28, 56, 91, 0)")
      ctx.fillStyle = grad
      ctx.beginPath()
      ctx.arc(0, 0, blob.r, 0, Math.PI * 2)
      ctx.fill()
      ctx.restore()
    }

    // Temporal noise overlay — cycles frames each render to dither banding
    ctx.globalAlpha = 0.08
    ctx.globalCompositeOperation = "overlay"
    ctx.drawImage(this.noiseFrames[this.noiseIndex], 0, 0, W, H)
    this.noiseIndex = (this.noiseIndex + 1) % this.noiseFrames.length
    ctx.globalAlpha = 1
    ctx.globalCompositeOperation = "source-over"
  }

  #drawPath({ p0, p1, p2, p3 }) {
    const { ctx } = this
    ctx.beginPath()
    ctx.moveTo(p0.x, p0.y)
    ctx.bezierCurveTo(p1.x, p1.y, p2.x, p2.y, p3.x, p3.y)
    ctx.strokeStyle = "rgba(160, 200, 255, 0.12)"
    ctx.lineWidth = 1
    ctx.stroke()
  }

  #drawDots(path) {
    const { ctx } = this
    for (const dot of path.dots) {
      const pos  = this.#bezierPoint(path.p0, path.p1, path.p2, path.p3, dot.t)
      // Fade in during first 6% of the path, fade out during the last 6%
      const fade = Math.min(dot.t / 0.06, 1) * Math.min((1 - dot.t) / 0.06, 1)
      ctx.beginPath()
      ctx.arc(pos.x, pos.y, dot.size, 0, Math.PI * 2)
      ctx.fillStyle = `rgba(56, 120, 205, ${0.85 * fade})`
      ctx.fill()
    }
  }

  #renderFrame() {
    this.#drawMesh()
    for (const path of this.paths) {
      this.#drawPath(path)
      this.#drawDots(path)
    }
  }

  #frame(now) {
    const dt = Math.min(now - this.lastTime, 50) // cap so tab-backgrounding doesn't jump
    this.lastTime = now

    for (const path of this.paths) {
      for (const dot of path.dots) {
        dot.t = (dot.t + path.speed * dt) % 1
      }
    }
    this.#renderFrame()

    this.animFrameId = requestAnimationFrame(this.boundFrame)
  }
}
